import React, { Fragment, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import AdminOrderDetails from './AdminOrderDetails'
import SimpleButton from '../common/SimpleButton'
import Dropdown from '../common/Dropdown'
import LoadingSpinner from '../common/LoadingSpinner'
import SearchWithFilter from '../common/SearchWithFilter'
import EmptyPageResults from '../common/EmptyPageResults'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import GenericTable from '../common/GenericTable'
import ActionsMenu from '../common/ActionsMenu'
import ExpandableText from '../common/ExpandableText'
import ConfirmationModal from '../common/ConfirmationModal'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, CheckCircleFilledIcon, CanceledIcon, PendingIcon } from '../../icons'
import { toast } from '../common/Toast'
import { useWindowSize } from '../../libs/hooks'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'
import { formatStoreProductPrice } from '../../utils/filters/filters'
import { convertTimeWithTAndZ } from '../../utils/filters/date'
import { loadSubscriptionHistory, reactivateSubscription, cancelSubscription, clearAdminOrdersMessages } from '../../actions/adminOrderHistory'
import { getCustomerOrderById, getAdminSubscriptionHistoryCsv } from '../../utils/requests/productsAPI'
import { downloadFile } from '../../utils/functions'
import './admin-subscription-history.scss'

const orderingSubscriptionFields = [
  {
    field: 'customerEmail',
    order: 'asc',
    translationKey: 'customer_asc',
  },
  {
    field: 'customerEmail',
    order: 'desc',
    translationKey: 'customer_desc',
  },
  {
    field: 'priceRecurrenceInterval',
    order: 'asc',
    translationKey: 'type_asc',
  },
  {
    field: 'priceRecurrenceInterval',
    order: 'desc',
    translationKey: 'type_desc',
  },
  {
    field: 'startDate',
    order: 'asc',
    translationKey: 'start_date_asc',
  },
  {
    field: 'startDate',
    order: 'desc',
    translationKey: 'start_date_desc',
  },
  {
    field: 'endDate',
    order: 'asc',
    translationKey: 'end_date_asc',
  },
  {
    field: 'endDate',
    order: 'desc',
    translationKey: 'end_date_desc',
  },
  {
    field: 'paymentAmount',
    order: 'asc',
    translationKey: 'renewal_price_asc',
  },
  {
    field: 'paymentAmount',
    order: 'desc',
    translationKey: 'renewal_price_desc',
  },
  {
    field: 'cancelAtPeriodEnd',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'cancelAtPeriodEnd',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const hasEnabledDailyPlans = process.env.REACT_APP_ONE_DAY_SUBSCRIPTION_TOGGLE?.toLowerCase() === 'on'

const Subscriptions = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [subscriptionsTableData, setSubscriptionsTableData] = useState([])
  const [showSpinner, setShowSpinner] = useState(null)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState({ field: 'startDate', order: 'desc', translationKey: 'startDate_desc' })
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [startDateFilter, setStartDateFilter] = useState(null)
  const [endDateFilter, setEndDateFilter] = useState(null)
  const [selectedFilterPlanTypes, setSelectedFilterPlanTypes] = useState([])
  const [selectedFilterStatuses, setSelectedFilterStatuses] = useState([])
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [expandedSubscription, setExpandedSubscription] = useState(0)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [showChangeSubscriptionConfirmationModal, setShowChangeSubscriptionConfirmationModal] = useState(false)
  const [subscriptionToChange, setSubscriptionToChange] = useState(null)
  const planTypes = [...(hasEnabledDailyPlans ? ['day'] : []), 'month', 'year']
  const statuses = ['active', 'canceled', 'trialing', 'past_due', 'incomplete_expired']

  useEffect(() => {
    const tableData = props.subscriptions.map((subscription, index) => {
      return {
        index,
        identifier: subscription.id,
        customerId: subscription.customerUuid,
        userOrderId: subscription.userOrderId,
        customerEmail: subscription.customerEmail,
        title: subscription.product?.title,
        type: subscription.priceRecurrenceInterval ? t(`coltene_store.admin_order_history.${subscription.priceRecurrenceInterval}_plan`) : '',
        startDate: convertTimeWithTAndZ(subscription.startDate, false, false, true),
        endDate: convertTimeWithTAndZ(subscription.endDate, false, false, true),
        renewalPrice: formatStoreProductPrice(subscription.paymentAmount, subscription.paymentCurrency),
        status: subscription.cancelAtPeriodEnd ? 'canceled' : subscription.status,
        actions: [
          {
            label: 'store_context.view_complete_order',
            onActionClick: (e) => {
              e.stopPropagation()
              handleViewCompleteOrder(subscription.customerUuid, subscription.userOrderId)
            },
          },
          {
            label: 'store_context.reactivate_subscription',
            onActionClick: (e) => {
              e.stopPropagation()
              setSubscriptionToChange({
                subscriptionId: subscription.id,
                userUuid: subscription.customerUuid,
                cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
              })
              setShowChangeSubscriptionConfirmationModal(true)
            },
            isDisabled: !subscription.cancelAtPeriodEnd,
          },
          {
            label: 'cancel',
            onActionClick: (e) => {
              e.stopPropagation()
              setSubscriptionToChange({
                subscriptionId: subscription.id,
                userUuid: subscription.customerUuid,
                cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
              })
              setShowChangeSubscriptionConfirmationModal(true)
            },
            isDisabled: subscription.cancelAtPeriodEnd,
          },
        ],
      }
    })

    setSubscriptionsTableData(tableData)
  }, [props.subscriptions])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearAdminOrdersMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearAdminOrdersMessages()
    }
  }, [props.successMessage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadSubscriptions(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, selectedFilterPlanTypes, selectedFilterStatuses, startDateFilter, endDateFilter, orderBy])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadSubscriptions(false)
    }
  }, [selectedPage])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  const createQueryParams = () => {
    let queryParams = { offset: 0 }

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (startDateFilter) {
      queryParams = { ...queryParams, startDate: startDateFilter }
    }

    if (endDateFilter) {
      queryParams = { ...queryParams, endDate: endDateFilter }
    }

    if (selectedFilterPlanTypes.length > 0) {
      queryParams = { ...queryParams, plan: selectedFilterPlanTypes }
    }

    if (selectedFilterStatuses.length > 0) {
      queryParams = { ...queryParams, status: selectedFilterStatuses }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingSubscriptionFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleResetFilters = () => {
    setSelectedFilterPlanTypes([])
    setSelectedFilterStatuses([])
    setStartDateFilter(null)
    setEndDateFilter(null)
  }

  const handleLoadSubscriptions = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      offset: withReset ? 0 : (selectedPage - 1) * process.env.REACT_APP_PAGINATION_SIZE,
    }

    props.actions.loadSubscriptionHistory(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingSubscriptionFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleChangeSubscription = () => {
    if (!subscriptionToChange) {
      return
    }

    if (subscriptionToChange.cancelAtPeriodEnd) {
      props.actions.reactivateSubscription(subscriptionToChange.subscriptionId, subscriptionToChange.userUuid)
    } else {
      props.actions.cancelSubscription(subscriptionToChange.subscriptionId, subscriptionToChange.userUuid)
    }
  }

  const handleViewCompleteOrder = (customerId, orderId) => {
    if (customerId && orderId) {
      setShowSpinner(true)

      getCustomerOrderById(customerId, orderId)
        .then((response) => {
          setSelectedOrder(response)

          setShowSpinner(false)
        })
        .catch(() => {
          setShowSpinner(false)

          toast.error(t('error.something_wrong'))
        })
    }
  }

  const exportToCsv = () => {
    if (showSpinner) {
      return
    }

    setShowSpinner(true)

    const queryParams = createQueryParams()

    getAdminSubscriptionHistoryCsv(queryParams)
      .then((res) => {
        const date = new Date().toISOString().split('T')
        const dateNow = date[0].split('-').reverse().join('_')
        const timeNow = date[1].split('.')[0].split(':').join('_')
        downloadFile({
          data: res,
          fileName: `subscriptions-history_${dateNow}_${timeNow}.csv`,
          fileType: 'text/csv',
        })
        setShowSpinner(false)

        toast.success(t('coltene_store.admin_order_history.subscriptions-download-success'))
      })
      .catch(() => {
        setShowSpinner(false)

        toast.error(t('error.failure_msg'))
      })
  }

  const closeDetailsModal = () => {
    setSelectedOrder(null)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  return (
    <Fragment>
      <div className="admin-subscriptions-history-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={selectedFilterPlanTypes.length > 0 || selectedFilterStatuses.length > 0 || (startDateFilter && endDateFilter)}
            counterBadge={selectedFilterPlanTypes.length + selectedFilterStatuses.length + (startDateFilter && endDateFilter ? 1 : 0)}
          />
          <SimpleButton className="action-button" onClick={() => exportToCsv()} disabled={showSpinner || !subscriptionsTableData.length > 0}>
            {t('coltene_store.admin_order_history.export-csv')}
          </SimpleButton>
        </div>
        {isMobile && subscriptionsTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              placeholder={t('select_ordering')}
              options={orderingSubscriptionFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && subscriptionsTableData.length > 0 && (
          <GenericTable
            data={subscriptionsTableData}
            headers={[
              {
                title: t('coltene_store.admin_order_history.customer'),
                orderKey: 'customerEmail',
              },
              {
                title: t('title'),
              },
              {
                title: t('type'),
                orderKey: 'priceRecurrenceInterval',
              },
              {
                title: t('start_date'),
                orderKey: 'startDate',
              },
              {
                title: t('end_date'),
                orderKey: 'endDate',
              },
              {
                title: t('renewal_price'),
                orderKey: 'paymentAmount',
              },
              {
                title: t('status'),
                orderKey: 'cancelAtPeriodEnd',
              },
              {
                title: '',
              },
            ]}
            keys={['customerEmail', 'title', 'type', 'startDate', 'endDate', 'renewalPrice', 'status', 'action']}
            keyRenderer={{
              startDate: (item) => {
                return (
                  <div className="date-wrapper">
                    <ExpandableText text={item.startDate} />
                  </div>
                )
              },
              endDate: (item) => {
                return (
                  <div className="date-wrapper">
                    <ExpandableText text={item.endDate} />
                  </div>
                )
              },
              status: (item) => {
                return (
                  <div className="status-wrapper">
                    {item.status === 'active' ? (
                      <CheckCircleFilledIcon className="success-svg" />
                    ) : item.status === 'trialing' ? (
                      <PendingIcon className="pending-svg" />
                    ) : (
                      <CanceledIcon className="cancel-svg" />
                    )}
                    <div className={`status ${item.status === 'active' ? 'processed' : 'cancelled-subscription'} d-flex flex-align-center`}>
                      {t([`filter_context.${item.status}`, item.status])}
                    </div>
                  </div>
                )
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(subscription) => handleViewCompleteOrder(subscription.customerId, subscription.userOrderId)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          subscriptionsTableData.map((subscription, index) => (
            <div key={index} className="card" onClick={() => setExpandedSubscription(index)}>
              <div className={'card-item' + (expandedSubscription !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('coltene_store.admin_order_history.customer')}</div>
                <div className={'card-item-body' + (expandedSubscription !== index ? ' align-center' : '')}>
                  <div>{subscription.customerEmail}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={subscription.actions} isLoading={showSpinner} />
                    )}
                  </div>
                </div>
              </div>
              <div className="card-item">
                <div className="card-item-title">{t('title')}</div>
                <div className="card-item-body">{subscription.title}</div>
              </div>
              {expandedSubscription === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('type')}</div>
                    <div className="card-item-body">{subscription.type}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('start_date')}</div>
                    <div className="card-item-body">{subscription.startDate}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('end_date')}</div>
                    <div className="card-item-body">{subscription.endDate}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('renewal_price')}</div>
                    <div className="card-item-body">{subscription.renewalPrice}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('status')}</div>
                    <div className="card-item-body card-item-body-status">
                      {subscription.status === 'active' ? <CheckCircleFilledIcon className="success-svg" /> : <CanceledIcon className="cancel-svg" />}
                      <div className={`status ${subscription.status === 'active' ? 'processed' : 'cancelled-subscription'} d-flex flex-align-center`}>
                        {subscription.status === 'active' ? t('active') : t('canceled')}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && subscriptionsTableData.length === 0 && (
          <EmptyPageResults title={t('coltene_store.admin_order_history.no-subscriptions-found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {selectedOrder && <AdminOrderDetails onCloseModal={closeDetailsModal} order={selectedOrder} />}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('date'),
              type: FilterType.dateRange,
              dataset: null,
              input: { startDate: startDateFilter, endDate: endDateFilter },
              output: (startDate, endDate) => {
                setStartDateFilter(startDate)
                setEndDateFilter(endDate)
              },
            },
            {
              name: t('type'),
              type: FilterType.multiSelect,
              dataset: planTypes,
              input: selectedFilterPlanTypes,
              output: (filterPlanTypes) => {
                setSelectedFilterPlanTypes(filterPlanTypes)
              },
            },
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: statuses,
              input: selectedFilterStatuses,
              output: (filterStatuses) => {
                setSelectedFilterStatuses(filterStatuses)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showChangeSubscriptionConfirmationModal && (
        <ConfirmationModal
          onToggle={() => setShowChangeSubscriptionConfirmationModal(false)}
          onAccept={() => {
            setShowChangeSubscriptionConfirmationModal(false)
            handleChangeSubscription()
          }}
          onCancel={() => setShowChangeSubscriptionConfirmationModal(false)}
          message={t('store_context.confirm_change_subscription')}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ adminOrderHistory }) {
  return {
    subscriptions: adminOrderHistory?.subscriptions || [],
    isLoading: adminOrderHistory?.subscriptionsLoading || adminOrderHistory?.subscriptionStatusesLoading,
    canLoadMore: adminOrderHistory?.canLoadMore,
    errorMessage: adminOrderHistory?.errorMessage,
    successMessage: adminOrderHistory?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadSubscriptionHistory,
        reactivateSubscription,
        cancelSubscription,
        clearAdminOrdersMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Subscriptions)
